import React, { useEffect, useState, useCallback } from "react";
import CustomTable from "src/components/App/AppGrid/CustomTable/CustomTable";
import { Avatar, Chip, Box, alpha, LinearProgress, Card, CardContent, Typography, IconButton, Collapse } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PendingIcon from "@mui/icons-material/Pending";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import theme from "src/config/theme";
import ButtonComponent from "src/components/shared/Button";
import DialogBox from "src/components/shared/Dialog/DialogBox";
import { ReactComponent as Edit } from '../../../../../assets/images/edit-2.svg';
import { ReactComponent as Delete } from '../../../../../assets/images/trash.svg';
import { ReactComponent as Add } from '../../../../../assets/images/add-square.svg'
import PaymentTabForm from "../Forms/PaymentTabForm";
import NoDataFound from "../../NoDataFound/NoDataFound";
import { useDispatch, useSelector } from "react-redux";
import { getBillingsByPatientIdRequest } from "src/modules/app/store/appActions";
import useResponsive from "src/components/hooks/useResponsive";
import useAppointmentDetails from "../../UseAppointmentDetails";
import { AllTabsMainContainer } from "../Style";
import PaymentCard from "../CardView/PaymentCard";

import dayjs from "dayjs";
import RefundForm from "../Forms/RefundForm";
const columns = [
  { id: "paymentfor", label: "Payment For", sx: { width: "426px", minWidth: "150px", textAlign: 'left' } },
  { id: "amount", label: "Amount", sx: { "& .css-axw7ok": { justifyContent: 'center' } }, },
  { id: "addedDate", label: "Added Date", sx: { "& .css-axw7ok": { justifyContent: 'center' } } },
  { id: "paymenttype", label: "Payment Type", sx: { "& .css-axw7ok": { justifyContent: 'center' } } },
  { id: "paymentmethod", label: "Payment Method", sx: { "& .css-axw7ok": { justifyContent: 'center' } } }
];


const Payment = ({ shouldRefresh }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [openOutstanding, setOpenOutstanding] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const dispatch = useDispatch();
  const [payment, setPayment] = useState([]);
  const { PatientId } = useAppointmentDetails();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const Width = isMobile || isTablet ? 18 : 24;
  const Height = isMobile || isTablet ? 18 : 24;
  const handleRefundOpen = (paymentId, paymenttype) => {
    setSelectedPaymentId(paymentId);
    setSelectedPaymentType(paymenttype)
    setOpenPayment(true);
  }
  const handleRefundClose = () => {
    setSelectedPaymentId(null);
    setSelectedPaymentType(null);
    setOpenPayment(false);
  }
  const handleOutstandingOpen = () => setOpenOutstanding(true);
  const handleOutstandingClose = () => setOpenOutstanding(false);
  const fetchPayments = useCallback(
    (page = 0, perPage = 10) => {
      const fetchData = async () => {
        const params = {
          page: page + 1,
          limit: perPage,
        };

        const response = await new Promise((resolve, reject) => {
          dispatch(getBillingsByPatientIdRequest({ patientId: PatientId, params }, resolve, reject));
        });

        if (response?.data && response?.meta) {
          setPayment(response.data);
          setTotalCount(response.meta.totalRecords);

        } else {
          console.log('Error fetching Payments');
        }
      }
      fetchData();

    },
    [dispatch, PatientId],
  );
  useEffect(() => {
    if (PatientId) {
      fetchPayments(page, perPage);
    }
  }, [PatientId, fetchPayments, page, perPage, shouldRefresh]);

  useEffect(() => {
    const handleBillingCreated = () => {
      fetchPayments(page, perPage);
    };
    window.addEventListener('app:billingCreated', handleBillingCreated);
    return () => {
      window.removeEventListener('app:billingCreated', handleBillingCreated);
    };
  }, [fetchPayments, page, perPage]);



  const actions = (row) => {
    if (!row) return [];

    return row.paymenttype === 'credit'
      ? [
        {
          label: "Refund",
          icon: <Edit width={Width} height={Height} />,
          onClick: () => handleRefundOpen(row.paymentId, row.paymenttype),
        }
      ]
      : [];
  };


  const data = payment.map((paymentData) => {
    const isRefund = paymentData.amount < 0;
    const isCredit = paymentData.type === 'credit';

    return {
      paymentId: paymentData.id,
      paymentfor: paymentData.paymentFor,
      amount: (
        <Box sx={{ color: isRefund ? 'red' : 'green', fontWeight: 'bold' }}>
          {isRefund ? `- ${Math.abs(paymentData.amount)}` : `+ ${paymentData.amount}`}
        </Box>
      ),
      addedDate: dayjs(paymentData.date).format("DD-MM-YYYY"),
      paymenttype: paymentData.type,
      paymentmethod: paymentData.method,
      disableActions: !isCredit,

    };
  });

  const sessions = (() => {
    const map = {};
    payment.forEach((p) => {
      const sid = p.tokenAppointment || 'unknown';
      if (!map[sid]) {
        map[sid] = { charges: 0, credits: 0, refunds: 0, entries: [] };
      }
      if (p.type === 'credit') {
        map[sid].credits += Math.abs(Number(p.amount) || 0);
      } else if (p.type === 'debit') {
        if (String(p.paymentFor).toLowerCase() === 'refund') {
          map[sid].refunds += Math.abs(Number(p.amount) || 0);
        } else {
          // Count all debit entries (e.g., Total Entry, consultation, x-ray) as charges
          map[sid].charges += Math.abs(Number(p.amount) || 0);
        }
      }
      map[sid].entries.push(p);
    });
    return map;
  })();

  const overallCharges = Object.values(sessions).reduce((sum, s) => sum + s.charges, 0);
  const overallCredits = Object.values(sessions).reduce((sum, s) => sum + s.credits, 0);
  const overallRefunds = Object.values(sessions).reduce((sum, s) => sum + s.refunds, 0);
  const overallNetPaid = Math.max(overallCredits - overallRefunds, 0); // credits minus refunds, never negative
  const overallRemaining = Math.max(overallCharges - overallNetPaid, 0);
  const overallPaid = overallCredits; // display total credits as "Total Paid"
  const overallPercent = overallCharges > 0 ? Math.round((overallNetPaid / overallCharges) * 100) : 0;

  const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

  const OutstandingDialog = () => {
    const sessionList = Object.entries(sessions);
    return (
      <Box sx={{ width: 480, px: 2, py: 1 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>Outstanding Payment</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
          <Chip label={`Total Sessions: ${sessionList.length}`} />
          <Chip color="primary" label={`Due Amount: ${fmt(overallCharges)}`} />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Payment Status</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip icon={<CheckCircleIcon sx={{ color: 'inherit' }} />} color="success" label={`Paid: ${fmt(overallPaid)}`} />
            <Chip icon={<PendingIcon sx={{ color: 'inherit' }} />} color="warning" label={`Remaining: ${fmt(overallRemaining)}`} />
          </Box>
          <Box sx={{ height: 14, width: '100%', borderRadius: 10, overflow: 'hidden', display: 'flex' }}>
            <Box sx={{ height: '100%', width: `${overallPercent}%`, bgcolor: alpha(theme.palette.success.main, 0.9) }} />
            <Box sx={{ height: '100%', width: `${100 - overallPercent}%`, bgcolor: alpha(theme.palette.warning.main, 0.7) }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {sessionList.map(([sid, s], idx) => {
            const netPaid = Math.max(s.credits - s.refunds, 0);
            const remaining = Math.max(s.charges - netPaid, 0);
            const paid = Math.min(netPaid, s.charges);
            const percent = s.charges > 0 ? Math.round((paid / s.charges) * 100) : 0;
            const status =
              netPaid >= s.charges ? 'Paid' :
                netPaid > 0 ? 'Partially Paid' : 'Pending';
            const color =
              status === 'Paid' ? 'success' :
                status === 'Partially Paid' ? 'warning' : 'default';
            const latest = s.entries.sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())[0];
            return (
              <Card key={sid} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{`Session ${idx + 1}`}</Typography>
                    <Chip
                      color={color}
                      icon={
                        status === 'Paid' ? <CheckCircleIcon /> :
                          status === 'Partially Paid' ? <HourglassEmptyIcon /> : <PendingIcon />
                      }
                      label={`${status}${status === 'Partially Paid' ? ` • ${percent}%` : ''}`}
                    />
                    <Typography variant="body2">{latest ? dayjs(latest.date).format('DD MMM YYYY') : ''}</Typography>
                  </Box>
                  <Box sx={{ height: 12, width: '100%', borderRadius: 10, overflow: 'hidden', bgcolor: alpha(theme.palette.warning.main, 0.2) }}>
                    <Box sx={{ height: '100%', width: `${percent}%`, bgcolor: alpha(theme.palette.success.main, 0.9) }} />
                  </Box>
                  <Typography variant="body2">{`Paid: ${fmt(paid)} • Remaining: ${fmt(remaining)}`}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <ButtonComponent
                      variant="outlined"
                      onClick={() => { }}
                      sx={{ minWidth: 140 }}
                    >
                      Send Reminder
                    </ButtonComponent>
                    <ButtonComponent
                      variant="contained"
                      onClick={() => {
                        setOpen(true);
                      }}
                      sx={{ minWidth: 160 }}
                    >
                      Make Payment
                    </ButtonComponent>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
        {overallRemaining > 0 && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: theme.palette.warning.light, borderRadius: 2 }}>
            <Typography variant="body2">{`Partial Payment Received: ${fmt(overallPaid)} out of ${fmt(overallCharges)}. Remaining Balance: ${fmt(overallRemaining)}`}</Typography>
          </Box>
        )}
      </Box>
    );
  };


  return (
    <>
      <AllTabsMainContainer sx={{ justifyContent: 'flex-start', height: 'auto', gap: 0 }}>
        {/* ================= BILLING SUMMARY ================= */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: 2,
            px: 2,
            pt: 2,
            pb: 1,
            mb: 0,
            boxShadow: theme.shadows[1],
            border: "1px solid #eee",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Billing Summary
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="body2">Total Charges</Typography>
              <Typography variant="h6">{fmt(overallCharges)}</Typography>
            </Box>

            <Box>
              <Typography variant="body2">Total Paid</Typography>
              <Typography variant="h6" color="success.main">
                {fmt(overallPaid)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2">Refunds</Typography>
              <Typography variant="h6" color="error.main">
                {fmt(overallRefunds)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2">Outstanding</Typography>
              <Typography variant="h6" color="warning.main">
                {fmt(overallRemaining)}
              </Typography>
            </Box>
          </Box>

          {/* Progress */}
          <Box sx={{ mt: 1, pb: 0 }}>
            <LinearProgress
              variant="determinate"
              value={overallPercent}
              sx={{ height: 8, borderRadius: 5 }}
            />
            {/* Removed caption to eliminate extra vertical space */}
          </Box>

          {/* Removed per requirement: session-level details and actions */}
        </Box>

        <Box
          sx={{
            background: "#fff",
            borderRadius: 2,
            px: 2,
            pt: 1,
            pb: 2,
            mt: 0,
            boxShadow: theme.shadows[1],
            border: "1px solid #eee",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Transaction History
            </Typography>
            <IconButton
              onClick={() => setShowHistory((v) => !v)}
              size="small"
              sx={{
                transform: showHistory ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
          <Collapse in={showHistory}>
            <CustomTable
              key={page}
              columns={columns}
              data={data}
              actions={actions}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage);
                setPage(0);
              }}
              perPage={perPage}
              totalCount={totalCount}
              headerButton={
                <ButtonComponent
                  variant="contained"
                  onClick={handleOpen}
                  sx={{
                    minWidth: "140px",
                    height: "36px",
                    borderRadius: "8px",
                  }}
                >
                  Add Payment
                </ButtonComponent>
              }
              noDataComponent={
                <NoDataFound
                  title="No Payments Found!"
                  buttonText="Add Payment"
                  buttonIcon={Add}
                  onClick={handleOpen}
                />
              }
            />
          </Collapse>
        </Box>

        {/* ================= DIALOGS ================= */}
        <DialogBox open={open} onClose={handleClose} title="Add Payment">
          <PaymentTabForm
            onClose={handleClose}
            fetchPayments={fetchPayments}
            defaultCollectAmount={overallRemaining}
          />
        </DialogBox>

        <DialogBox
          open={openPayment}
          onClose={handleRefundClose}
          title="Refund Payment"
        >
          <RefundForm
            open={openPayment}
            onClose={handleRefundClose}
            fetchPayments={fetchPayments}
            PaymentId={selectedPaymentId}
          />
        </DialogBox>
      </AllTabsMainContainer>
    </>
  )
};

export default Payment;
